import React, { useMemo, useState, useEffect, useCallback } from 'react';
import Sidebar from './common/Sidebar';
import Header from './common/Header';
import { useTranslation } from 'react-i18next';
import { Treemap, Tooltip, ResponsiveContainer } from 'recharts';
import { useFetch } from '../hooks/useFetch';

const PALETTE = ['#6b8afd', '#8a6bfd', '#5fd0a5', '#f6a84f', '#ff7676', '#4fc3f7', '#9575cd', '#81c784', '#ffd54f', '#ff8a65'];

// Custom cell renderer to color categories differently
const CustomizedContent = (props) => {
  const { depth, x, y, width, height, index, name, size } = props;
  // Hide root container label/rect entirely
  if (depth === 0 || name === 'Total') return null;
  // All visible tiles are top-level categories in our setup => use palette
  const color = PALETTE[index % PALETTE.length];
  const labelColor = '#fff';
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={8}
        ry={8}
        style={{ fill: color, stroke: '#ffffffaa', strokeWidth: 1 }}
      />
      {width > 100 && height > 26 && (
        <text
          x={x + 10}
          y={y + 18}
          fill={labelColor}
          fontSize={12}
          fontWeight={700}
          style={{ paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.25)', strokeWidth: 1 }}
        >
          {name}
        </text>
      )}
      {width > 120 && height > 44 && (
        <text
          x={x + 10}
          y={y + 36}
          fill={labelColor}
          fontSize={11}
          style={{ paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.2)', strokeWidth: 0.8 }}
        >
          {Number(size || 0).toLocaleString('es-CL')}
        </text>
      )}
    </g>
  );
};

const fmtCLP = (n, locale='es-CL') =>
  Number(n || 0).toLocaleString(locale, { style: 'currency', currency: 'CLP' });

export default function TreemapView() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || 'es').startsWith('es') ? 'es-CL' : 'en-US';

  const [period, setPeriod] = useState('month');
  const [monthRef, setMonthRef] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  const [dashboardData] = useFetch('/dashboard', { defaultValue: [] });
  const [expensesData]  = useFetch('/transactions', { defaultValue: [], hasPaging: true, defaultLimit: 2000 });
  const [goalsData]      = useFetch('/goals', { defaultValue: [] });

  const dash = (dashboardData && dashboardData.data) ? dashboardData.data : dashboardData;
  const goalsMap = useMemo(() => {
    const res = {};
    const data = goalsData && goalsData.data ? goalsData.data : goalsData;
    if (Array.isArray(data)) {
      data.forEach(g => {
        const catName = g?.category_name || g?.category?.name || g?.name;
        const goalVal = Number(g?.goal ?? g?.amount ?? g?.value);
        if (catName && !isNaN(goalVal)) res[catName] = goalVal;
      });
    } else if (data && typeof data === 'object') {
      // fallback if backend returns an object map { category: goal }
      Object.entries(data).forEach(([k,v]) => {
        const n = Number(v);
        if (!isNaN(n)) res[k] = n;
      });
    }
    return res;
  }, [goalsData]);

  const expenses = useMemo(() => {
    const d = expensesData;
    return Array.isArray(d?.transactions)
      ? d.transactions
      : (Array.isArray(d) ? d : []);
  }, [expensesData]);

  // Build a sorted list of months (Date objects at day 1) that actually have expenses
  const availableMonths = useMemo(() => {
    const set = new Set();
    expenses.forEach(e => {
      const dt = new Date(e.date);
      if (isNaN(dt)) return;
      const k = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}`;
      set.add(k);
    });
    return Array.from(set)
      .map(k => { const [y,m] = k.split('-'); return new Date(Number(y), Number(m)-1, 1); })
      .sort((a,b) => a - b);
  }, [expenses]);

  const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;

  const findPrevMonthWithData = useCallback((ref) => {
    const refMonth = new Date(ref.getFullYear(), ref.getMonth(), 1);
    let prev = null;
    for (const d of availableMonths) {
      if (d < refMonth) prev = d; else break;
    }
    return prev;
  }, [availableMonths]);

  const findNextMonthWithData = useCallback((ref) => {
    const refMonth = new Date(ref.getFullYear(), ref.getMonth(), 1);
    for (const d of availableMonths) {
      if (d > refMonth) return d;
    }
    return null;
  }, [availableMonths]);

  // If current month has no data when switching to Month view, snap to the latest month with data
  useEffect(() => {
    if (period !== 'month') return;
    if (!availableMonths.length) return;
    const has = availableMonths.some(d => monthKey(d) === monthKey(monthRef));
    if (!has) setMonthRef(availableMonths[availableMonths.length - 1]);
  }, [period, availableMonths, monthRef]);

  const prevMonth = () => setMonthRef(m => {
    const p = findPrevMonthWithData(m);
    return p || m; // if none, stay
  });
  const nextMonth = () => setMonthRef(m => {
    const n = findNextMonthWithData(m);
    return n || m; // if none, stay
  });

  const prevYear = () => setMonthRef(m => new Date(m.getFullYear() - 1, 0, 1));
  const nextYear = () => setMonthRef(m => new Date(m.getFullYear() + 1, 0, 1));

  const inWindow = useCallback((d) => {
    const dt = new Date(d);
    if (period === 'month') {
      return dt.getFullYear() === monthRef.getFullYear() && dt.getMonth() === monthRef.getMonth();
    }
    if (period === 'year') {
      return dt.getFullYear() === monthRef.getFullYear();
    }
    return true;
  }, [monthRef, period]);

  const inPrevWindow = useCallback((d) => {
    const dt = new Date(d);
    if (period === 'month') {
      const prev = findPrevMonthWithData(monthRef);
      if (!prev) return false;
      return dt.getFullYear() === prev.getFullYear() && dt.getMonth() === prev.getMonth();
    }
    if (period === 'year') {
      // prev year that actually has data
      const years = Array.from(new Set(availableMonths.map(m => m.getFullYear()))).sort((a,b)=>a-b);
      const currY = monthRef.getFullYear();
      const prevY = years.filter(y => y < currY).pop();
      if (!prevY && prevY !== 0) return false;
      return dt.getFullYear() === prevY;
    }
    return false; // 'all' has no previous window
  }, [availableMonths, findPrevMonthWithData, monthRef, period]);
  const prevPeriodLabel = () => {
    if (period === 'month') {
      const prev = findPrevMonthWithData(monthRef);
      return prev ? prev.toLocaleDateString(locale, { month: 'long', year: 'numeric' }) : t('common.none','—');
    }
    if (period === 'year') {
      const years = Array.from(new Set(availableMonths.map(m => m.getFullYear()))).sort((a,b)=>a-b);
      const currY = monthRef.getFullYear();
      const prevY = years.filter(y => y < currY).pop();
      return (prevY || prevY === 0) ? String(prevY) : t('common.none','—');
    }
    return t('common.none','—');
  };

  const treeData = useMemo(() => {
    const curr = new Map();
    const prev = new Map();

    const categoriesMap = dash?.categories || {};

    const add = (map, cat, amt, date) => {
      const m = map.get(cat) || { amount: 0, count: 0, maxAmount: 0, maxDate: null };
      m.amount += amt;
      m.count += 1;
      if (amt > m.maxAmount) { m.maxAmount = amt; m.maxDate = date; }
      map.set(cat, m);
    };

    expenses.forEach(e => {
      const cat = e?.data?.subcategory?.category?.name || 'Otros';
      const amt = Number(e.amount) || 0;
      if (inWindow(e.date)) add(curr, cat, amt, e.date);
      else if (inPrevWindow(e.date)) add(prev, cat, amt, e.date);
    });

    // Build children list from curr; if vacío, fallback a categoriesMap
    const children = (curr.size
      ? Array.from(curr.entries()).map(([name, m]) => ({ name, size: m.amount }))
      : Object.entries(categoriesMap).map(([name, val]) => ({ name, size: Number(val) || 0 }))
    ).sort((a,b) => (b.size||0) - (a.size||0));

    const result = [{ name: t('map.total','Total'), children }];
    result._metrics = { curr, prev };
    return result;
  }, [expenses, dash, t, inWindow, inPrevWindow]);

  const categoriesList = useMemo(() => {
    const root = treeData[0];
    const items = (root?.children || []).map((c, idx) => {
      const name = c.name;
      const currMap = treeData._metrics?.curr || new Map();
      const prevMap = treeData._metrics?.prev || new Map();
      const cm = currMap.get(name) || { amount: 0, count: 0, maxAmount: 0, maxDate: null };
      const pm = prevMap.get(name) || { amount: 0, count: 0, maxAmount: 0, maxDate: null };
      const delta = cm.amount - pm.amount;
      const deltaPct = pm.amount ? (delta / pm.amount) : (cm.amount ? 1 : 0);
      const avg = cm.count ? (cm.amount / cm.count) : 0;
      return {
        name,
        amount: cm.amount,
        count: cm.count,
        avg,
        maxAmount: cm.maxAmount,
        maxDate: cm.maxDate,
        prevAmount: pm.amount,
        delta,
        deltaPct,
        index: idx,
        pct: 0 // will be filled below
      };
    });
    const total = items.reduce((s,x)=>s+x.amount,0);
    const prevTotal = items.reduce((s,x)=>s+x.prevAmount,0);
    const totalDelta = total - prevTotal;
    const totalDeltaPct = prevTotal ? (totalDelta / prevTotal) : (total ? 1 : 0);

    return { total, prevTotal, totalDelta, totalDeltaPct, items };
  }, [treeData]);

  const periodLabel = useMemo(() => {
    if (period === 'month') return monthRef.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    if (period === 'year')  return monthRef.toLocaleDateString(locale, { year: 'numeric' });
    return t('common.all','Todos');
  }, [period, monthRef, locale, t]);

  return (
    <div className="dashboard-container overview">
      <Header title={t('map.title','Mapa de gastos')} />
      <div className="dashboard-four-columns">
        <Sidebar />
        <div className="dashboard-main">

          <div className="dashboard-section section-wide" style={{ gridColumn:'1 / -1' }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
              <div className="transfers-toggle-group">
                <button
                  className={period==='month'?'active':''}
                  aria-pressed={period==='month'}
                  style={period==='month' ? { fontWeight: 700, opacity: 1 } : {}}
                  onClick={()=>setPeriod('month')}
                >
                  {t('common.month','Mes')}
                </button>
                <button
                  className={period==='year'?'active':''}
                  aria-pressed={period==='year'}
                  style={period==='year' ? { fontWeight: 700, opacity: 1 } : {}}
                  onClick={()=>setPeriod('year')}
                >
                  {t('common.year','Año')}
                </button>
                <button
                  className={period==='all'?'active':''}
                  aria-pressed={period==='all'}
                  style={period==='all' ? { fontWeight: 700, opacity: 1 } : {}}
                  onClick={()=>setPeriod('all')}
                >
                  {t('common.all','Todos')}
                </button>
              </div>
              {period==='month' && (
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <button onClick={prevMonth}>◀</button>
                  <strong style={{ textTransform:'capitalize' }}>{periodLabel}</strong>
                  <button onClick={nextMonth}>▶</button>
                </div>
              )}
              {period==='year' && (
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <button onClick={prevYear}>◀</button>
                  <strong>{periodLabel}</strong>
                  <button onClick={nextYear}>▶</button>
                </div>
              )}
            </div>
          </div>

          <div className="dashboard-section section-wide" style={{ gridColumn:'1 / -1', display:'grid', gridTemplateColumns:'2fr 1fr', gap:16 }}>
            <div
              className="dashboard-card"
              style={{
                height: 640,
                borderRadius: 12,
                border: '1px solid rgba(0,0,0,0.06)',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.95) 100%)',
                boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div
                style={{
                  padding: '10px 14px',
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  color: '#1f2937',
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0))',
                  borderBottom: '1px solid rgba(0,0,0,0.06)'
                }}
              >
                {t('map.composition','Composición de gastos')}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {t('map.vsPrev','vs')} {prevPeriodLabel()}: {fmtCLP(categoriesList.prevTotal, locale)} → {fmtCLP(categoriesList.total, locale)}
                <span
                  style={{
                    marginLeft: 6,
                    color:
                      (categoriesList.prevTotal === 0 || categoriesList.totalDelta === 0)
                        ? '#6b7280'
                        : (categoriesList.totalDelta > 0 ? '#dc2626' : '#059669')
                  }}
                >
                  {categoriesList.prevTotal === 0
                    ? '— 0.0%'
                    : (categoriesList.totalDelta > 0
                        ? `▲ ${(Math.abs(categoriesList.totalDeltaPct) * 100).toFixed(1)}%`
                        : (categoriesList.totalDelta < 0
                            ? `▼ ${(Math.abs(categoriesList.totalDeltaPct) * 100).toFixed(1)}%`
                            : '— 0.0%'))}
                </span>
              </div>
              <div style={{ flex: 1 }}>
                {(treeData[0]?.children?.length ?? 0) === 0 && (
                  <div style={{ padding: 12, color: '#666' }}>{t('map.noData','Sin datos para el período seleccionado')}</div>
                )}
                <ResponsiveContainer width="100%" height="100%">
                  <Treemap
                    data={treeData}
                    dataKey="size"
                    ratio={4/3}
                    stroke="rgba(0,0,0,0.08)"
                    isAnimationActive={false}
                    content={<CustomizedContent />}
                  >
                    <Tooltip
                      content={({ payload }) => {
                        const node = payload && payload[0] && payload[0].payload;
                        if (!node || node.name === 'Total') return null;
                        const name = node.name;
                        const currMap = treeData._metrics?.curr || new Map();
                        const pm = (treeData._metrics?.prev || new Map()).get(name) || { amount: 0 };
                        const cm = currMap.get(name) || { amount: 0, count: 0, maxAmount: 0, maxDate: null };
                        const avg = cm.count ? (cm.amount / cm.count) : 0;
                        const delta = cm.amount - pm.amount;
                        const deltaPct = pm.amount ? (delta / pm.amount) : (cm.amount ? 1 : 0);
                        return (
                          <div
                            style={{
                              background: '#fff',
                              border: '1px solid rgba(0,0,0,0.1)',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                              padding: '10px 12px',
                              borderRadius: 10,
                              minWidth: 220
                            }}
                          >
                            <div style={{ fontWeight: 700, color: '#111827', marginBottom: 6 }}>{name}</div>
                            <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', rowGap:4, columnGap:10, color:'#374151' }}>
                              <span>{t('map.amount','Monto')}:</span><span>{fmtCLP(cm.amount, locale)}</span>
                              <span>{t('map.count','Transacciones')}:</span><span>{cm.count}</span>
                              <span>{t('map.avg','Ticket promedio')}:</span><span>{fmtCLP(avg, locale)}</span>
                              {cm.maxAmount ? (<>
                                <span>{t('map.max','Gasto máx.')}:</span><span>{fmtCLP(cm.maxAmount, locale)} {cm.maxDate ? `(${new Date(cm.maxDate).toLocaleDateString(locale)})` : ''}</span>
                              </>) : null}
                              <span>{t('map.vsPrevShort','vs prev.')}:</span>
                              <span>
                                {fmtCLP(pm.amount, locale)} → {fmtCLP(cm.amount, locale)}{' '}
                                <span
                                  style={{
                                    marginLeft: 6,
                                    color: (pm.amount === 0 || delta === 0) ? '#6b7280' : (delta > 0 ? '#dc2626' : '#059669')
                                  }}
                                >
                                  {pm.amount === 0
                                    ? '— 0.0%'
                                    : (delta > 0
                                        ? `▲ ${(Math.abs(deltaPct) * 100).toFixed(1)}%`
                                        : (delta < 0
                                            ? `▼ ${(Math.abs(deltaPct) * 100).toFixed(1)}%`
                                            : '— 0.0%'))}
                                </span>
                              </span>
                            </div>
                          </div>
                        );
                      }}
                    />
                  </Treemap>
                </ResponsiveContainer>
              </div>
            </div>

            <aside className="dashboard-card" style={{ borderRadius:8, border:'1px solid rgba(0,0,0,0.08)', padding:12 }}>
              <div style={{ fontWeight:600, marginBottom:8 }}>{t('map.categories','Categorías')}</div>
              <div style={{ opacity:.7, marginBottom:6 }}>{t('map.total','Total')}: {fmtCLP(categoriesList.total, locale)}</div>
              <ul style={{ listStyle:'none', padding:0, margin:0, display:'grid', gap:8 }}>
                {categoriesList.items.map((x) => (
                  <li key={x.name} style={{ display:'grid', gridTemplateColumns:'auto 1fr auto', alignItems:'center', gap:10 }}>
                    <span style={{ width:10, height:10, borderRadius:'50%', background: PALETTE[x.index % PALETTE.length] }} />
                    <span style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{x.name}</span>
                    <span style={{ textAlign:'right' }}>
                      <div style={{ fontVariantNumeric:'tabular-nums' }}>{fmtCLP(x.amount, locale)} <span style={{ opacity:.6 }}>({(x.pct*100).toFixed(1)}%)</span></div>
                      <div
                        style={{
                          fontSize: 11,
                          opacity: .8,
                          color: (x.prevAmount === 0 || x.delta === 0) ? '#6b7280' : (x.delta > 0 ? '#dc2626' : '#059669')
                        }}
                      >
                        {t('map.vsPrevShort','vs prev.')}: {fmtCLP(x.prevAmount, locale)} → {fmtCLP(x.amount, locale)}
                        {' '}
                        <span>
                          {x.prevAmount === 0
                            ? '— 0.0%'
                            : (x.delta > 0
                                ? `▲ ${(Math.abs(x.deltaPct) * 100).toFixed(1)}%`
                                : (x.delta < 0
                                    ? `▼ ${(Math.abs(x.deltaPct) * 100).toFixed(1)}%`
                                    : '— 0.0%'))}
                        </span>
                      </div>
                      {goalsMap[x.name] ? (
                        <div style={{ marginTop:6 }}>
                          <div style={{ height:6, background:'#e5e7eb', borderRadius:3 }}>
                            <div style={{
                              width: `${Math.min((x.amount / goalsMap[x.name]) * 100, 100)}%`,
                              height: '100%',
                              background: x.amount >= goalsMap[x.name] ? '#059669' : '#60a5fa',
                              borderRadius: 3,
                              transition: 'width 160ms ease'
                            }} />
                          </div>
                          <div style={{ fontSize:10, opacity:.7, marginTop:2 }}>
                            {fmtCLP(x.amount, locale)} / {fmtCLP(goalsMap[x.name], locale)}
                          </div>
                        </div>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
              {categoriesList.items.length === 0 && (
                <div style={{ opacity:.7 }}>{t('map.noData','Sin datos para el período seleccionado')}</div>
              )}
            </aside>
          </div>

        </div>
      </div>
    </div>
  );
}